export type LangMode = "km" | "en" | "zh";

// ----------------- SLIDER TICKS CONFIGURATION -----------------
export interface SliderTick {
  label: string;
  members: number;
  monthlyTxs: number;
  campaigns: number;
  card2Price: number;
  card2Original: number;
  card2Monthly: string;
  card3Price: number;
  card3Original: number;
  card3Monthly: string;
}

export const SLIDER_TICKS: SliderTick[] = [
  {
    label: "0.3K",
    members: 300,
    monthlyTxs: 300,
    campaigns: 30,
    card2Price: 1199,
    card2Original: 2199,
    card2Monthly: "33.30",
    card3Price: 959,
    card3Original: 2919,
    card3Monthly: "26.64",
  },
  {
    label: "0.5K",
    members: 500,
    monthlyTxs: 500,
    campaigns: 50,
    card2Price: 1699,
    card2Original: 2999,
    card2Monthly: "47.19",
    card3Price: 1499,
    card3Original: 4199,
    card3Monthly: "41.64",
  },
  {
    label: "1K",
    members: 1000,
    monthlyTxs: 1000,
    campaigns: 100,
    card2Price: 2759,
    card2Original: 4769,
    card2Monthly: "76.64",
    card3Price: 2699,
    card3Original: 6999,
    card3Monthly: "74.97",
  },
  {
    label: "2K",
    members: 2000,
    monthlyTxs: 2000,
    campaigns: 200,
    card2Price: 4899,
    card2Original: 8999,
    card2Monthly: "136.08",
    card3Price: 4799,
    card3Original: 12999,
    card3Monthly: "133.30",
  },
  {
    label: "3K",
    members: 3000,
    monthlyTxs: 3000,
    campaigns: 300,
    card2Price: 6899,
    card2Original: 12999,
    card2Monthly: "191.64",
    card3Price: 6799,
    card3Original: 17999,
    card3Monthly: "188.86",
  },
];

// ----------------- FEATURE STRUCTURE -----------------
export interface FeatureItem {
  checked: boolean;
  text: string;
  badge?: string;
  boldPart?: string;
}

export interface FeatureSection {
  title: string;
  badge?: string;
  items: FeatureItem[];
  footerNote?: string;
}

export interface PartnerDiscountItem {
  name: string;
  discount: string;
}

export interface CardContent {
  tierBadge: string;
  title: string;
  desc: string;
  priceUnit: string;
  termNote: string;
  ctaText: string;
  subNote: string;
  sections: FeatureSection[];
  partnerDiscountsTitle: string;
  partnerDiscountsBanner: string;
  partnerDiscounts: PartnerDiscountItem[];
}

export interface I18nSchema {
  currency: string;
  headline: string;
  discountBadge: string;
  tabSingle: string;
  tabChain: string;
  durationMonth: string;
  duration1Yr: string;
  duration2Yr: string;
  durationGiftBadge: string;
  activeBadge: string;
  selectBadge: string;
  perMonth: string;
  noAutoRenew: string;
  skipBtn: string;
  processing: string;
  card1TierStd: string;
  card1TierAdv: string;
  card1Spec: string;
  card1SpecAdv: string;
  card2Members: (count: number) => string;
  card3Branches: (count: number) => string;
  card1: CardContent;
  card2: CardContent;
  card3: CardContent;
}

export const PLAN_I18N: Record<LangMode, I18nSchema> = {
  // =========================================================================
  // 1. KHMER (ភាសាខ្មែរ)
  // =========================================================================
  km: {
    currency: "$",
    headline: "ជ្រើសរើសគម្រោងអាជីវកម្ម",
    discountBadge: "បញ្ចុះតម្លៃរហូតដល់ 70%",
    tabSingle: "ហាងទោល",
    tabChain: "ពហុសាខា",
    durationMonth: "ប្រចាំខែ",
    duration1Yr: "១ ឆ្នាំ",
    duration2Yr: "២ ឆ្នាំ",
    durationGiftBadge: "ថែម ១ ឆ្នាំ",
    activeBadge: "បានជ្រើសរើស",
    selectBadge: "ចុចជ្រើសរើស",
    perMonth: "/ខែ",
    noAutoRenew: "ដំណើរការភ្លាមៗ មិនកាត់ប្រាក់ស្វ័យប្រវត្តិ",
    skipBtn: "រំលងទៅកាន់ផ្ទាំងគ្រប់គ្រង →",
    processing: "កំពុងដំណើរការ...",

    card1TierStd: "១ សាខា",
    card1TierAdv: "៣ សាខា",
    card1Spec: "POS: 100 tx/ខែ • SMS: 20 សារ/ខែ",
    card1SpecAdv: "POS: 300 tx/ខែ • SMS: 60 សារ/ខែ",

    card2Members: (count: number) => `${count} សមាជិកសកម្ម / ខែ`,
    card3Branches: (count: number) => `${count} ស្ថានីយសាខាហាង`,

    card1: {
      tierBadge: "ចាប់ផ្តើម",
      title: "Starter",
      desc: "ស័ក្តិសមសម្រាប់ហាងទោល ឬហាងកាហ្វេ",
      priceUnit: "/៣៦ ខែ",
      termNote: "គិតប្រាក់ ៣៦ ខែ",
      ctaText: "ជ្រើសរើស Starter",
      subNote: "ដំណើរការភ្លាមៗ មិនកាត់ប្រាក់ស្វ័យប្រវត្តិ",
      sections: [
        {
          title: "ប្រព័ន្ធពិន្ទុសមាជិកស្នូល (Core Loyalty)",
          items: [
            { checked: true, text: "សិទ្ធិប្រើប្រាស់អាជីវកម្មពាណិជ្ជកម្មស្របច្បាប់" },
            { checked: true, text: "កាតសមាជិកឌីជីថល QR Code ផ្តល់ & ប្តូរពិន្ទុ" },
            { checked: true, text: "ស្ថានីយគិតលុយ Cloud POS", boldPart: "១០០ tx/ខែ" },
            { checked: true, text: "ផ្ញើសារ SMS ជូនដំណឹងពិន្ទុអតិថិជន", boldPart: "២០ សារ/ខែ" },
            { checked: true, text: "ថែមជូនពិសេស៖ បន្ថែម ២០ SMS/ខែ រយៈពេល ៣៦ ខែ", boldPart: "៣៦ ខែ" },
          ],
          footerNote: "* រួមបញ្ចូលទាំងកម្មវិធី Cloud POS និងប្រព័ន្ធផ្ញើសារ SMS ផ្លូវការ",
        },
        {
          title: "យុទ្ធនាការទីផ្សារ & កាដូ (Marketing & Campaigns)",
          items: [
            { checked: true, text: "កំណត់កម្រិតសមាជិក (Bronze, Silver, Gold)" },
            { checked: false, text: "ប្រព័ន្ធគូប៉ុងស្វ័យប្រវត្តិ & កាដូថ្ងៃកំណើត" },
            { checked: false, text: "ប្រព័ន្ធទីផ្សារស្វ័យប្រវត្តិ & ផ្ញើសារជូនដំណឹងជាក្រុម" },
            { checked: false, text: "ការបញ្ចុះតម្លៃលើកញ្ចប់ពិន្ទុ & SMS បន្ថែម" },
          ],
        },
        {
          title: "ការតភ្ជាប់ប្រព័ន្ធ & API (Integrations)",
          items: [
            { checked: false, text: "តភ្ជាប់ REST API & Webhooks សម្រាប់ប្រព័ន្ធផ្ទាល់ខ្លួន" },
            { checked: false, text: "តភ្ជាប់ជាមួយ Telegram & WhatsApp Loyalty Mini-App" },
            { checked: false, text: "រចនាវិក្កយបត្រ & កម្មវិធីសមាជិកផ្ទាល់ខ្លួនតាម Brand" },
            { checked: false, text: "ប្រព័ន្ធទូទាត់ពិន្ទុឆ្លងសាខាច្រើនទីតាំង" },
          ],
        },
        {
          title: "ទំហំផ្ទុកទិន្នន័យលើ Cloud (Storage & Ledger)",
          items: [
            { checked: true, text: "ទំហំផ្ទុកទិន្នន័យលើ Cloud សុវត្ថិភាព 50 GB" },
          ],
        },
      ],
      partnerDiscountsTitle: "ឧបករណ៍ & ការតភ្ជាប់ដែលគាំទ្រ",
      partnerDiscountsBanner: "ភាពត្រូវគ្នាជាមួយឧបករណ៍ POS ខាងក្រៅ",
      partnerDiscounts: [
        { name: "ម៉ាស៊ីនស្កេនបាកូដ QR Barcode Scanner", discount: "គាំទ្រ ១០០% ⓘ" },
        { name: "ម៉ាស៊ីនព្រីនវិក្កយបត្រកម្ដៅ Cloud Receipt Printer", discount: "គាំទ្រ ១០០% ⓘ" },
        { name: "ថតដាក់ប្រាក់ស្វ័យប្រវត្តិ Cash Drawer Sync", discount: "គាំទ្រ ១០០% ⓘ" },
        { name: "កម្មវិធីលើថេប្លេត iPad / Android POS App", discount: "គាំទ្រ ១០០% ⓘ" },
        { name: "ប្រព័ន្ធការពារទិន្នន័យ Off-line Cashier Cache", discount: "គាំទ្រ ១០០% ⓘ" },
      ],
    },

    card2: {
      tierBadge: "ពេញនិយមបំផុត",
      title: "Growth Pro",
      desc: "ប្រព័ន្ធទីផ្សារស្វ័យប្រវត្តិ និងបង្កើនសមាជិក",
      priceUnit: "/៣៦ ខែ",
      termNote: "គិតប្រាក់ ៣៦ ខែ",
      ctaText: "ជ្រើសរើស Pro",
      subNote: "ដំណើរការភ្លាមៗ មិនកាត់ប្រាក់ស្វ័យប្រវត្តិ",
      sections: [
        {
          title: "ប្រព័ន្ធពិន្ទុសមាជិកស្នូល (Core Loyalty)",
          items: [
            { checked: true, text: "មុខងារទាំងអស់ក្នុង Starter ត្រូវបានរួមបញ្ចូល" },
            { checked: true, text: "គាំទ្រ 300 ដល់ 3,000 សមាជិកសកម្ម/ខែ", boldPart: "300 - 3,000" },
            { checked: true, text: "កាតសមាជិកឌីជីថល QR Code មិនកំណត់ចំនួនស្កេន" },
            { checked: true, text: "ស្ថានីយគិតលុយ Cloud POS គ្មានដែនកំណត់" },
          ],
        },
        {
          title: "យុទ្ធនាការទីផ្សារ & កាដូ (Marketing & Campaigns)",
          items: [
            { checked: true, text: "ប្រព័ន្ធគូប៉ុងស្វ័យប្រវត្តិ & កាដូថ្ងៃកំណើត (Birthday Gifts)" },
            { checked: true, text: "យុទ្ធនាការកងបង្វិលសំណាង Spin-to-Win និងកាត VIP" },
            { checked: true, text: "ផ្ញើសារជូនដំណឹងផ្សព្វផ្សាយទីផ្សារតាមកាលវិភាគ" },
            { checked: true, text: "ទទួលបានការបញ្ចុះតម្លៃពិសេសលើកញ្ចប់ពិន្ទុបន្ថែម" },
          ],
        },
        {
          title: "ការតភ្ជាប់ប្រព័ន្ធ & API (Integrations)",
          items: [
            { checked: true, text: "តភ្ជាប់ជាមួយ Telegram & WhatsApp Loyalty Mini-App" },
            { checked: true, text: "ប្តូររង្វាន់ និងទូទាត់ពិន្ទុឆ្លងសាខាច្រើនទីតាំង" },
            { checked: true, text: "ទាញយករបាយការណ៍កម្រិតស្តង់ដារ CSV/Excel" },
            { checked: false, text: "តភ្ជាប់ REST API & Webhooks សម្រាប់ប្រព័ន្ធផ្ទាល់ខ្លួន" },
            { checked: false, text: "រចនាវិក្កយបត្រ & Logo តាម Brand ផ្ទាល់ខ្លួន" },
          ],
        },
        {
          title: "ទំហំផ្ទុកទិន្នន័យលើ Cloud (Storage & Ledger)",
          items: [
            { checked: true, text: "ទំហំផ្ទុកទិន្នន័យលើ Cloud សុវត្ថិភាព 50 GB" },
          ],
        },
      ],
      partnerDiscountsTitle: "ការបញ្ចុះតម្លៃលើសេវាបន្ថែម & API",
      partnerDiscountsBanner: "អត្ថប្រយោជន៍ផ្ដាច់មុខ 11.11",
      partnerDiscounts: [
        { name: "Telegram Loyalty Mini-App", discount: "បញ្ចុះតម្លៃរហូតដល់ 40% ⓘ" },
        { name: "WhatsApp CRM & Notifications", discount: "បញ្ចុះតម្លៃរហូតដល់ 40% ⓘ" },
        { name: "ច្រកតភ្ជាប់ Cloud POS ពហុសាខា", discount: "បញ្ចុះតម្លៃរហូតដល់ 40% ⓘ" },
        { name: "AI វិភាគអាកប្បកិរិយាអតិថិជន RFM", discount: "ឥតគិតថ្លៃ ៥ ដង/ថ្ងៃ ⓘ" },
        { name: "Bot បង្កើតគូប៉ុងទីផ្សារស្វ័យប្រវត្តិ", discount: "ឥតគិតថ្លៃ ៥ ដង/ថ្ងៃ ⓘ" },
      ],
    },

    card3: {
      tierBadge: "សហគ្រាស VIP",
      title: "Enterprise VIP",
      desc: "គ្រប់គ្រងសាខាកណ្តាល និងតភ្ជាប់ API ពេញលេញ",
      priceUnit: "/៣៦ ខែ",
      termNote: "គិតប្រាក់ ៣៦ ខែ",
      ctaText: "ជ្រើសរើស VIP",
      subNote: "ដំណើរការភ្លាមៗ មិនកាត់ប្រាក់ស្វ័យប្រវត្តិ",
      sections: [
        {
          title: "ប្រព័ន្ធពិន្ទុសមាជិកស្នូល (Core Loyalty)",
          badge: "ថែមជូនពិសេស",
          items: [
            { checked: true, text: "មុខងារទាំងអស់ក្នុង Growth Pro ត្រូវបានរួមបញ្ចូល" },
            { checked: true, text: "គ្រប់គ្រងសាខាហាង និងបញ្ជរគិតលុយកណ្តាលមិនកំណត់" },
            { checked: true, text: "ថែមជូនកូតាស្ថានីយសាខាផ្លូវការ ១០០ សាខា/ខែ រយៈពេល ៣ ខែ" },
            { checked: true, text: "ថែមជូនសារ SMS & Notification ដៃគូ ២០ សារ/ខែ រយៈពេល ៣ ខែ" },
          ],
        },
        {
          title: "យុទ្ធនាការទីផ្សារ & កាដូ (Marketing & Campaigns)",
          items: [
            { checked: true, text: "ចូលប្រើប្រាស់គ្រប់ម៉ូឌុលទីផ្សារ និង AI Campaign Generator" },
            { checked: true, text: "ប្រព័ន្ធគូប៉ុង & កាតាឡុករង្វាន់កម្រិតខ្ពស់មិនកំណត់" },
            { checked: true, text: "យុទ្ធនាការឆ្លងម៉ាកយីហោ (Cross-Brand Partnerships)" },
            { checked: true, text: "ការបញ្ចុះតម្លៃខ្ពស់បំផុតលើកញ្ចប់ពិន្ទុ និង SMS" },
          ],
        },
        {
          title: "ការតភ្ជាប់ប្រព័ន្ធ & API (Integrations)",
          items: [
            { checked: true, text: "តភ្ជាប់ REST API & Webhooks គ្មានដែនកំណត់", boldPart: "Full Access" },
            { checked: true, text: "រចនាកម្មវិធីសមាជិក & វិក្កយបត្រតាម Brand និង Logo ផ្ទាល់ខ្លួន" },
            { checked: true, text: "វិស្វករប្រឹក្សាបច្ចេកទេស និងដំឡើងប្រព័ន្ធផ្ទាល់ 24/7 SLA" },
            { checked: true, text: "ប្រព័ន្ធទូទាត់ពិន្ទុឆ្លងសាខាកម្រិតសហគ្រាស (Real-time Clearing)" },
          ],
        },
        {
          title: "ទំហំផ្ទុកទិន្នន័យលើ Cloud (Storage & Ledger)",
          items: [
            { checked: true, text: "ទំហំផ្ទុកទិន្នន័យលើ Cloud សុវត្ថិភាព 50 GB" },
          ],
        },
      ],
      partnerDiscountsTitle: "អត្ថប្រយោជន៍ផ្ដាច់មុខ VIP Enterprise",
      partnerDiscountsBanner: "កញ្ចប់សេវាកម្មកម្រិតខ្ពស់ (Enterprise SLA)",
      partnerDiscounts: [
        { name: "វិស្វករប្រឹក្សាបច្ចេកទេសផ្ទាល់", discount: "គាំទ្រ 24/7 SLA ⓘ" },
        { name: "Open REST API & Webhooks", discount: "ឥតគិតថ្លៃ ១០០% ⓘ" },
        { name: "White-label Brand Customization", discount: "ឥតគិតថ្លៃ ១០០% ⓘ" },
        { name: "Multi-Store Real-time Clearing", discount: "គាំទ្រ ១០០% ⓘ" },
        { name: "Dedicated Account Manager", discount: "ផ្តល់ជូនពិសេស ⓘ" },
      ],
    },
  },

  // =========================================================================
  // 2. ENGLISH
  // =========================================================================
  en: {
    currency: "$",
    headline: "Choose Your Plan",
    discountBadge: "Save Up to 70%",
    tabSingle: "Single Store",
    tabChain: "Multi-Store Chains",
    durationMonth: "Monthly",
    duration1Yr: "1 Year",
    duration2Yr: "2 Years",
    durationGiftBadge: "+1 Yr Free",
    activeBadge: "Selected Plan",
    selectBadge: "Click to Select",
    perMonth: "/mo",
    noAutoRenew: "Instant activation, no auto-renewal",
    skipBtn: "Skip for now →",
    processing: "Processing...",

    card1TierStd: "1 Outlet",
    card1TierAdv: "3 Outlets",
    card1Spec: "POS: 100 tx/mo • SMS: 20 msg/mo",
    card1SpecAdv: "POS: 300 tx/mo • SMS: 60 msg/mo",

    card2Members: (count: number) => `${count} Active Members / mo`,
    card3Branches: (count: number) => `${count} Branch Outlets`,

    card1: {
      tierBadge: "Starter Tier",
      title: "Starter",
      desc: "Best for single boutique stores or cafes",
      priceUnit: "/36 mos",
      termNote: "Billed for 36 months",
      ctaText: "Select Starter",
      subNote: "Instant activation, no auto-renewal",
      sections: [
        {
          title: "Core Member Engine",
          items: [
            { checked: true, text: "Commercial business license & standalone operations" },
            { checked: true, text: "Digital QR member cards with instant point issue & redeem" },
            { checked: true, text: "Standard Cloud Cashier POS application", boldPart: "100 tx/mo" },
            { checked: true, text: "Member SMS point update notifications", boldPart: "20 msg/mo" },
            { checked: true, text: "Bonus launch quota: +20 SMS alerts/mo for 36 months", boldPart: "36 Mos" },
          ],
          footerNote: "* Includes official Cloud Cashier POS and verified SMS gateway delivery",
        },
        {
          title: "Marketing & Rewards Engine",
          items: [
            { checked: true, text: "Multi-tier membership hierarchy (Bronze, Silver, Gold)" },
            { checked: false, text: "Automated birthday rewards & digital coupon campaigns" },
            { checked: false, text: "Marketing automation triggers & bulk customer messaging" },
            { checked: false, text: "Exclusive wholesale discounts on points & SMS bundles" },
          ],
        },
        {
          title: "Enterprise Integrations",
          items: [
            { checked: false, text: "Open REST API & Webhooks access for custom architectures" },
            { checked: false, text: "Telegram & WhatsApp Loyalty Mini-App connectivity" },
            { checked: false, text: "Custom digital receipt branding & customer portal white-label" },
            { checked: false, text: "Multi-store cross-branch clearing & settlement" },
          ],
        },
        {
          title: "Cloud Ledger & Storage",
          items: [
            { checked: true, text: "50 GB secure cloud ledger and transaction history storage" },
          ],
        },
      ],
      partnerDiscountsTitle: "Hardware & POS Compatibility",
      partnerDiscountsBanner: "Verified External Hardware Support",
      partnerDiscounts: [
        { name: "2D QR & Barcode Scanners", discount: "100% Compatible ⓘ" },
        { name: "Cloud Thermal Receipt Printers", discount: "100% Compatible ⓘ" },
        { name: "Electronic Cash Drawer Sync", discount: "100% Compatible ⓘ" },
        { name: "iPad & Android Tablet POS App", discount: "100% Compatible ⓘ" },
        { name: "Offline Cashier Cache Mode", discount: "100% Compatible ⓘ" },
      ],
    },

    card2: {
      tierBadge: "Most Popular",
      title: "Growth Pro",
      desc: "Automated loyalty marketing & member growth",
      priceUnit: "/36 mos",
      termNote: "Billed for 36 months",
      ctaText: "Select Pro",
      subNote: "Instant activation, no auto-renewal",
      sections: [
        {
          title: "Core Member Engine",
          items: [
            { checked: true, text: "All features in Starter included" },
            { checked: true, text: "Scale from 300 to 3,000 active members/mo", boldPart: "300 - 3,000" },
            { checked: true, text: "Unlimited digital QR member card scans & passes" },
            { checked: true, text: "Full Cloud Cashier POS with unlimited checkouts" },
          ],
        },
        {
          title: "Marketing & Rewards Engine",
          items: [
            { checked: true, text: "Automated digital coupons & birthday gift engine" },
            { checked: true, text: "Spin-to-Win lucky gamification & VIP tier cards" },
            { checked: true, text: "Scheduled marketing notifications & segment blasts" },
            { checked: true, text: "Preferred volume pricing on add-on points & SMS" },
          ],
        },
        {
          title: "Enterprise Integrations",
          items: [
            { checked: true, text: "Telegram & WhatsApp CRM Mini-App connect" },
            { checked: true, text: "Cross-store points redemption across branches" },
            { checked: true, text: "Standard CSV/Excel ledger reporting export" },
            { checked: false, text: "Open REST API & Webhooks access for custom backend" },
            { checked: false, text: "Custom brand identity & receipt typography styling" },
          ],
        },
        {
          title: "Cloud Ledger & Storage",
          items: [
            { checked: true, text: "50 GB secure cloud ledger and transaction history storage" },
          ],
        },
      ],
      partnerDiscountsTitle: "Add-on Services & Ecosystem Discounts",
      partnerDiscountsBanner: "Exclusive 11.11 Growth Perks",
      partnerDiscounts: [
        { name: "Telegram Loyalty Mini-App", discount: "Up to 40% Off ⓘ" },
        { name: "WhatsApp CRM & Notifications", discount: "Up to 40% Off ⓘ" },
        { name: "Multi-Store Cloud POS Gateway", discount: "Up to 40% Off ⓘ" },
        { name: "AI Customer RFM Behavior Insights", discount: "Free 5x/day ⓘ" },
        { name: "Automated Campaign Generator Bot", discount: "Free 5x/day ⓘ" },
      ],
    },

    card3: {
      tierBadge: "Enterprise VIP",
      title: "Enterprise VIP",
      desc: "Central chain middle-office & custom APIs",
      priceUnit: "/36 mos",
      termNote: "Billed for 36 months",
      ctaText: "Select VIP",
      subNote: "Instant activation, no auto-renewal",
      sections: [
        {
          title: "Core Member Engine",
          badge: "VIP Bonus",
          items: [
            { checked: true, text: "All features in Growth Pro included" },
            { checked: true, text: "Unlimited central multi-store brand management" },
            { checked: true, text: "Bonus store terminal quota: 100 outlets for 3 months" },
            { checked: true, text: "Bonus SMS & partner notification quota for 3 months" },
          ],
        },
        {
          title: "Marketing & Rewards Engine",
          items: [
            { checked: true, text: "Full access to marketing suite & AI Campaign Generator" },
            { checked: true, text: "Unlimited reward catalogs & customized digital vouchers" },
            { checked: true, text: "Cross-brand joint loyalty alliances & point swaps" },
            { checked: true, text: "Highest tier enterprise discounts on point & SMS bundles" },
          ],
        },
        {
          title: "Enterprise Integrations",
          items: [
            { checked: true, text: "Full access to open REST API & Webhooks", boldPart: "Full Access" },
            { checked: true, text: "Custom branded customer portal & digital receipt design" },
            { checked: true, text: "Dedicated technical onboarding architect & 24/7 SLA" },
            { checked: true, text: "Real-time enterprise cross-branch financial clearing" },
          ],
        },
        {
          title: "Cloud Ledger & Storage",
          items: [
            { checked: true, text: "50 GB secure cloud ledger and transaction history storage" },
          ],
        },
      ],
      partnerDiscountsTitle: "Enterprise SLA & Dedicated Services",
      partnerDiscountsBanner: "Premium SLA Guarantee",
      partnerDiscounts: [
        { name: "Dedicated Technical Architect", discount: "24/7 SLA ⓘ" },
        { name: "Open REST API & Webhooks", discount: "100% Included ⓘ" },
        { name: "White-label Brand Customization", discount: "100% Included ⓘ" },
        { name: "Multi-Store Real-time Clearing", discount: "100% Included ⓘ" },
        { name: "Dedicated Account Manager", discount: "Priority ⓘ" },
      ],
    },
  },

  // =========================================================================
  // 3. CHINESE (中文)
  // =========================================================================
  zh: {
    currency: "¥",
    headline: "选择您的方案",
    discountBadge: "立省高达 70%",
    tabSingle: "单门店商户",
    tabChain: "连锁品牌企业",
    durationMonth: "连续包月",
    duration1Yr: "1年付",
    duration2Yr: "2年付",
    durationGiftBadge: "送1年",
    activeBadge: "当前选择",
    selectBadge: "点击选择",
    perMonth: "/月",
    noAutoRenew: "购买即时生效，不自动续费",
    skipBtn: "暂不选购，去逛逛 →",
    processing: "处理中...",

    card1TierStd: "单店档",
    card1TierAdv: "3店档",
    card1Spec: "收银: 100次/月 • 短信: 20条/月",
    card1SpecAdv: "收银: 300次/月 • 短信: 60条/月",

    card2Members: (count: number) => `${count} 活跃会员 / 月`,
    card3Branches: (count: number) => `${count} 连锁门店终端`,

    card1: {
      tierBadge: "入门起步",
      title: "Starter 基础版",
      desc: "适合单门店零售与餐饮咖啡店",
      priceUnit: "/36个月",
      termNote: "周期 36个月",
      ctaText: "选购 Starter",
      subNote: "购买即时生效，不自动续费",
      sections: [
        {
          title: "核心会员系统 (Core Loyalty)",
          items: [
            { checked: true, text: "商业化运营授权与单门店独立核算" },
            { checked: true, text: "电子会员卡、扫码领卡与积分存取核销" },
            { checked: true, text: "标准云收银结算应用", boldPart: "100次/月" },
            { checked: true, text: "会员短信变动即时提醒", boldPart: "20条/月" },
            { checked: true, text: "限时加送：额外 20条短信/月 赠送 36个月", boldPart: "36个月" },
          ],
          footerNote: "* 包含官方云收银收银台及官方短信通道服务",
        },
        {
          title: "营销与权益中心 (Marketing)",
          items: [
            { checked: true, text: "多等级会员体系 (白银/黄金/铂金)" },
            { checked: false, text: "自动发放生日礼券与会员卡券中心" },
            { checked: false, text: "自动化营销推送与会员分组触达" },
            { checked: false, text: "充值积分与短信包专属折扣" },
          ],
        },
        {
          title: "系统对接与开放接口 (Integrations)",
          items: [
            { checked: false, text: "开放 REST API 与 Webhooks 接口接入" },
            { checked: false, text: "对接 Telegram 与 WhatsApp 会员小程序" },
            { checked: false, text: "专属品牌定制会员界面与小票排版" },
            { checked: false, text: "连锁多门店跨店积分消费与结算" },
          ],
        },
        {
          title: "云端存储与安全账本 (Cloud Storage)",
          items: [
            { checked: true, text: "50 GB 安全加密云端账目存储" },
          ],
        },
      ],
      partnerDiscountsTitle: "支持的外部硬件与生态",
      partnerDiscountsBanner: "官方认证硬件兼容支持",
      partnerDiscounts: [
        { name: "2D 扫码枪与条码识别仪", discount: "100% 兼容 ⓘ" },
        { name: "云端热敏小票打印机", discount: "100% 兼容 ⓘ" },
        { name: "智能联动电子钱箱", discount: "100% 兼容 ⓘ" },
        { name: "iPad 与安卓平板收银 App", discount: "100% 兼容 ⓘ" },
        { name: "离线断网收银保护机制", discount: "100% 兼容 ⓘ" },
      ],
    },

    card2: {
      tierBadge: "最受青睐",
      title: "Growth Pro 专业版",
      desc: "自动化营销与会员生命周期增长",
      priceUnit: "/36个月",
      termNote: "周期 36个月",
      ctaText: "开通 Pro 版",
      subNote: "购买即时生效，不自动续费",
      sections: [
        {
          title: "核心会员系统 (Core Loyalty)",
          items: [
            { checked: true, text: "包含 Starter 基础版所有功能" },
            { checked: true, text: "支持 300 至 3,000 活跃会员弹性扩展", boldPart: "300 - 3,000" },
            { checked: true, text: "无限次电子会员卡出示与扫码核销" },
            { checked: true, text: "专业版云收银台支持无限交易频次" },
          ],
        },
        {
          title: "营销与权益中心 (Marketing)",
          items: [
            { checked: true, text: "自动发放生日礼券与会员卡券中心" },
            { checked: true, text: "幸运大转盘、刮刮乐等互动营销小工具" },
            { checked: true, text: "定时营销推送与精细化客户人群包" },
            { checked: true, text: "增购积分与短信包专享批发折扣" },
          ],
        },
        {
          title: "系统对接与开放接口 (Integrations)",
          items: [
            { checked: true, text: "对接 Telegram 与 WhatsApp 会员通道" },
            { checked: true, text: "连锁多门店跨店积分通兑通销与清算" },
            { checked: true, text: "标准财务 CSV/Excel 账目报表导出" },
            { checked: false, text: "开放企业级 REST API 与 Webhooks" },
            { checked: false, text: "定制品牌专属界面与独立小票样式" },
          ],
        },
        {
          title: "云端存储与安全账本 (Cloud Storage)",
          items: [
            { checked: true, text: "50 GB 安全加密云端账目存储" },
          ],
        },
      ],
      partnerDiscountsTitle: "附加增值服务与生态权益",
      partnerDiscountsBanner: "双11 备战专享福利",
      partnerDiscounts: [
        { name: "Telegram 会员小程序对接", discount: "最高限时 6折 ⓘ" },
        { name: "WhatsApp CRM 消息通知通道", discount: "最高限时 6折 ⓘ" },
        { name: "多门店云收银终端接入", discount: "最高限时 6折 ⓘ" },
        { name: "AI 会员 RFM 价值分析模型", discount: "最高限免 5次/天 ⓘ" },
        { name: "AI 营销活动文案与卡券助手", discount: "最高限免 5次/天 ⓘ" },
      ],
    },

    card3: {
      tierBadge: "旗舰企业",
      title: "Enterprise 旗舰版",
      desc: "连锁多门店统一中台与 API 对接",
      priceUnit: "/36个月",
      termNote: "周期 36个月",
      ctaText: "开通 VIP 版",
      subNote: "购买即时生效，不自动续费",
      sections: [
        {
          title: "核心会员系统 (Core Loyalty)",
          badge: "VIP 特权",
          items: [
            { checked: true, text: "包含 Growth Pro 专业版所有功能" },
            { checked: true, text: "无限多品牌、多门店中央统一中台管理" },
            { checked: true, text: "限时加送官方门店直连终端：100家/月 赠送 3个月" },
            { checked: true, text: "限时加送第三方短信与通知：20条/月 赠送 3个月" },
          ],
        },
        {
          title: "营销与权益中心 (Marketing)",
          items: [
            { checked: true, text: "全功能开放营销套件与 AI 营销活动引擎" },
            { checked: true, text: "支持跨异业品牌积分联盟与联合卡券" },
            { checked: true, text: "无限制配置个性化积分兑换商城与卡券" },
            { checked: true, text: "享全平台最高等级短信与积分批发采购价" },
          ],
        },
        {
          title: "系统对接与开放接口 (Integrations)",
          items: [
            { checked: true, text: "全功能开放 REST API 与 Webhooks", boldPart: "Full Access" },
            { checked: true, text: "专属品牌定制会员小程序与小票排版" },
            { checked: true, text: "专属技术架构师 7x24h SLA 运维保障" },
            { checked: true, text: "企业级多门店跨店清算与独立财务报表" },
          ],
        },
        {
          title: "云端存储与安全账本 (Cloud Storage)",
          items: [
            { checked: true, text: "50 GB 安全加密云端账目存储" },
          ],
        },
      ],
      partnerDiscountsTitle: "企业专属保障与专家服务",
      partnerDiscountsBanner: "企业级 7x24h SLA 专家支持",
      partnerDiscounts: [
        { name: "专属技术架构师与一对一支持", discount: "24/7 SLA ⓘ" },
        { name: "开放 REST API 与 Webhooks", discount: "100% 包含 ⓘ" },
        { name: "White-label 品牌全套定制", discount: "100% 包含 ⓘ" },
        { name: "多门店跨店实时清算中台", discount: "100% 包含 ⓘ" },
        { name: "专属客户成功经理", discount: "VIP 优先 ⓘ" },
      ],
    },
  },
};
